import {
    Component,
    Input,
    OnInit,
    ViewChild,
    inject,
    signal,
    ChangeDetectionStrategy
} from '@angular/core';
import { HisNotificationService } from '../../../core/service/notifications/his-notification.service';
import { HisUploadFile } from '../../model/his-upload-file';
import { HisFileUploadComponent } from '../his-file-upload.component';
import { HisPlamComponent } from '../../../structure/component/plam/his-plam.component';
import { HisUiService } from '../../../core/service/base/his-ui.service';
import { GenericValue } from '../../../core/model/generic-value';
import { HisGenericValueService } from '../../../core/service/base/his-generic-value.service';
import * as _ from 'lodash';
import { HisFileUploadService } from '../../service/his-file-upload.service';
import { HisUploadConfiguration } from '../../model/his-upload-configuration';

/**
 * Company: HIS
 * @author Jan Bauer <jan.bauer-extern@his.de>
 */
@Component({
    selector: 'his-upload-list',
    templateUrl: './his-upload-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class HisUploadListComponent implements OnInit {
    private notificationService = inject(HisNotificationService);
    private valueService = inject(HisGenericValueService);
    uploadService = inject(HisFileUploadService);
    private uiService = inject(HisUiService);

    @Input() documents: HisUploadFile[] = [];
    @Input({ required: true }) configKey: string;

    @ViewChild('addFileInput') private addFileInput: HisFileUploadComponent;
    @ViewChild('addFilePlam') private addFilePlam: HisPlamComponent;

    protected displayAddFileDialog = false;
    protected newFiles: HisUploadFile[] = [];
    protected displayEditFileDialog = false;
    private uploadConfiguration: HisUploadConfiguration;
    protected allCategories: GenericValue[] = [];
    protected filteredCategories: GenericValue[] = [];
    protected editUui: string;
    protected editCategoryIdOriginal: number;
    protected editCategory: GenericValue;
    protected editComment: string;
    protected addButtonVisible = false;

    public ngOnInit(): void {
        this.uploadService
            .getUploadFieldConfig(this.configKey)
            .subscribe((response) => {
                this.uploadConfiguration = new HisUploadConfiguration(response);
                this.addButtonVisible = response && !response.readonly;
            });
        this.valueService
            .getValueTypes('documentMetadataCategoryValue')
            .subscribe((response) => (this.allCategories = response));
    }

    protected showAddFileDialog(): void {
        this.displayAddFileDialog = true;
        this.newFiles = [];
    }

    protected addFile(): void {
        // validate
        this.addFileInput.triggerValidation();
        if (this.addFilePlam.hasErrors()) {
            const errors = this.addFilePlam.getErrors();
            let errorMessages: string[] = [];
            for (const errorKey of Object.keys(errors)) {
                errorMessages.push(
                    this.uiService.translate(errors[errorKey].message),
                );
            }
            this.notificationService.errorI18n(errorMessages.join('\n'));
            return;
        }

        if (this.documents === null) {
            this.documents = [];
        }

        // add to documents
        this.documents.push( ...this.newFiles );

        // close dialog and show notification
        this.displayAddFileDialog = false;
        this.notificationService.infoI18n('de.his.javang.gefunden.change');
        this.uploadService.abortAllOnGoingFeedbackRequests();
    }

    protected hideAddFileDialog( uploadComponent: HisFileUploadComponent ): void {
        uploadComponent?.reset();
        this.displayAddFileDialog = false;
        this.uploadService.abortAllOnGoingFeedbackRequests();
    }

    protected showEditFileDialog($event: HisUploadFile): void {
        // init edit properties
        this.editUui = $event.uuid;
        this.editCategoryIdOriginal = $event.documentMetadataCategoryId;
        this.editCategory = this.allCategories.find(
            (cat) => cat.id === this.editCategoryIdOriginal,
        );
        this.editComment = $event.comment;

        // show dialog
        this.displayEditFileDialog = true;
    }

    protected showEditCategory(): boolean {
        if (!this.uploadConfiguration) {
            return false;
        }

        if (
            this.uploadConfiguration.documentMetadataCategoryIds.length > 1
        ) {
            return true;
        }

        if (
            !_.includes(
                this.uploadConfiguration.documentMetadataCategoryIds,
                this.editCategoryIdOriginal,
            )
        ) {
            return true;
        }

        return this.uploadConfiguration.showCategoryIfUnique;
    }

    protected showEditComment(): boolean {
        if (!this.uploadConfiguration) {
            return false;
        }

        if (!_.isEmpty(this.editComment)) {
            return true;
        }

        return this.uploadConfiguration.showDescription;
    }

    protected editCommentDisabled(): boolean {
        if (!this.uploadConfiguration) {
            return true;
        }

        return !this.uploadConfiguration.showDescription;
    }

    protected filterCategories(event: { query: string }): void {
        let categories = new Set<GenericValue>();
        for (let dmc of this.allCategories) {
            if (
                dmc.defaulttext
                    .toLowerCase()
                    .includes(event.query.toLowerCase()) &&
                (_.includes(
                    this.uploadConfiguration.documentMetadataCategoryIds,
                    dmc.id,
                ) ||
                    dmc.id === this.editCategoryIdOriginal)
            ) {
                categories.add(dmc);
            }
        }
        this.filteredCategories = [...categories];
    }

    protected updateFile(): void {
        const docIndex = this.documents.findIndex(
            (doc) => doc.uuid === this.editUui,
        );
        if (docIndex > -1) {
            // change detection requires a workaround to function properly
            // first we remove the old instance from documents and than add
            // a copy of the updated instance at the same index
            const docToUpdate = structuredClone(this.documents[docIndex]);
            docToUpdate.documentMetadataCategoryId = this.editCategory.id;
            docToUpdate.comment = this.editComment;
            docToUpdate.documentMetadataCategoryHiskeyId =
                this.editCategory.hiskeyId;
            this.documents.splice(docIndex, 1);
            this.documents.splice(docIndex, 0, docToUpdate);
        }

        //  close dialog and show notification
        this.displayEditFileDialog = false;
        this.notificationService.infoI18n('de.his.javang.gefunden.change');
    }

    protected deleteFile($event: HisUploadFile): void {
        const deleteIndex = this.documents.findIndex(
            (file) => file.uuid === $event.uuid,
        );
        if (deleteIndex > -1) {
            this.documents.splice(deleteIndex, 1);
            this.notificationService.infoI18n('de.his.javang.gefunden.change');
        }
    }

    protected showDocumentsTable(): boolean {
        return this.documents && this.documents.length > 0;
    }

    protected showCommentColumn(): boolean {
        if (!this.uploadConfiguration) {
            return false;
        }

        return this.uploadConfiguration.showDescription;
    }

    protected category(
        documentMetadataCategoryId: number,
        compareHisKeyId: boolean,
    ): string {
        if (!documentMetadataCategoryId) {
            return null;
        }
        let category;
        if (compareHisKeyId === true) {
            category = this.allCategories.find(
                (c) => c.hiskeyId === documentMetadataCategoryId,
            );
        } else {
            category = this.allCategories.find(
                (c) => c.id === documentMetadataCategoryId,
            );
        }
        return category?.defaulttext;
    }
}
